import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppSettings } from '../../shared/app.settings';
import { RestApiService } from '../../shared/services/rest-api.service';
import { StorageService } from './../../shared/services/storage.service';
import { BookingWorkflow } from '../models/booking-workflow';
import { PassengerContactUpdate } from '../models/notification-opt-in';
import { VehicleTransferDriver, VtFilters } from '../models/vehicle-transfer';

export interface AirportEligibility {
  airportEligible: boolean;
  airportLocationId: string | null;
}

/** TN-manage-booking-110-BE §3.1: the event dispatch zone a coordinate falls in, and the time zone to use there. */
export interface DispatchZoneResult {
  insideZone: boolean;
  tenantServiceAreaId: string | null;
  timezone: string | null;
}

/** TN-manage-booking-113-BE §3.2: the pickup slot's booking count and the event's Peak Period status. */
export interface BookingCapacityResult {
  checked: boolean;
  slotStartLocal: string | null;
  slotEndLocal: string | null;
  zoneId: string | null;
  bookingCount: number;
  maxBookings: number;
  limitReached: boolean;
  inPeak: boolean;
  peakMessage: string | null;
}

export interface BookingCapacityQuery {
  eventId: string;
  pickupLatitude: number;
  pickupLongitude: number;
  rideLater: boolean;
  pickupLocalDateTime?: string;
  excludeTourId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BookingService {
  bookingDetail: any = null;
  dedicatedBookingDetail: any = null;
  dedicatedBookingDetail$ = new BehaviorSubject<any>(null);
  obj: any;
  serviceTypeOptions: any[] = [];
  selectedService: any = '';
  statusOptions: any[] = [];
  selectedStatus: any = '';
  params: any = '';
  bookingTotalInfo = new Subject<any>();
  bookingDetailsRefresh = new Subject<any>();
  bookingInfo;

  packageApiError = [{
    driverId: false,
    serviceTypeId: false,
    partnerId: false,
    partnerType: false,
    phoneNum: false,
    passengerName: false,
    vehicleTypeId: false,
    vehicleId: false,
    packageId: false,
    bookingStatus: false,
    sourceLattitude: false,
    sourceLongitude: false,
    destinationLatitude: false,
    destinationLongitude: false,
    pickupLocationName: false,
    dropLocationName: false,
    pickupDateTime: false,
    passangerCount: false,
    vip: false,
    vehicleAutomatic: false,
    withFuel: false,
    note: false,
    general: false,
    makeModel: false,
  }];

  constructor(
    private restApiService: RestApiService,
    private storageService: StorageService
  ) { }

  addUpdateBooking(data, serviceType, id?): Observable<any> {
    return this.restApiService.post(`/secure/booking/${serviceType}`, data);
  }

  updateManualBookingInfo(data, bookingId): Observable<any> {
    return this.restApiService.put(`/secure/booking/manual/${bookingId}`, data, 'page-center');
  }

  packageBind(serviceType, passengerId?, vehicleTypeId?): Observable<any> {
    if (vehicleTypeId !== null && vehicleTypeId !== undefined) {
      const url = '/secure/packages/allpackages/'
        + this.storageService.getItemFromSessionStorage(AppSettings.SERVICE_AREA_ID) + '/' + serviceType +
        '?vehicleTypeId=' + vehicleTypeId + '&passengerId' + passengerId;
      return this.restApiService.get(url);
    } else {
      const url = '/secure/packages/allpackages/'
        + this.storageService.getItemFromSessionStorage(AppSettings.SERVICE_AREA_ID) + '/' + serviceType;
      return this.restApiService.get(url);
    }
  }

  searchDriver(url: string): Observable<any> {
    return this.restApiService.get(url);
  }

  searchPassengers(url: string): Observable<any> {
    return this.restApiService.get(url);
  }

  assignDriver(bookingId, data): Observable<any> {
    return this.restApiService.put('/secure/booking/' + bookingId + '/assign-driver', data);
  }

  cancelBooking(id, data): Observable<any> {
    return this.restApiService.put('/secure/booking/' + id + '/cancel', data);
  }

  noShowBooking(tourId, data): Observable<any> {
    return this.restApiService.put(`/secure/booking/${tourId}/cancel`, data);
  }

  bookingDetails(id): Observable<any> {
    return this.restApiService.get('/secure/booking/' + id);
  }

  fetchTourBooking(tourId, eventId, froDate?, toDate?): Observable<any> {
    return this.restApiService.get('/secure/tour/' + tourId + `?eventId=${eventId}&fromDate=${froDate ? froDate : ''}&toDate=${toDate ? toDate : ''}`);
  }

  startTrip(data, tripid): Observable<any> {
    return this.restApiService.put('/secure/booking/trip/' + tripid + '/start-trip', data);
  }

  endTrip(data, tripid): Observable<any> {
    return this.restApiService.put('/secure/booking/trip/' + tripid + '/end-trip', data);
  }

  cancelTrip(data, tripid): Observable<any> {
    return this.restApiService.put('/secure/booking/trip/' + tripid + '/cancel-trip', data);
  }

  resendCode(bookingId, passengerId): Observable<any> {
    return this.restApiService.put('/secure/booking/' + bookingId + '/verification-code');
  }

  getCities(eventId?): Observable<any> {
    return this.restApiService.get(`/secure/service-area/cities?eventId=${eventId}`);
  }

  getRegionByCityId(cityId): Observable<any> {
    return this.restApiService.get(`/secure/service-area/regions/${cityId}`);
  }

  sendEmailInvoice(tourId, data): Observable<any> {
    return this.restApiService.post(`/secure/tour-receipt/email/` + tourId, data, 'page-center');
  }

  sendBookingDetailsEmail(data): Observable<any> {
    return this.restApiService.put(`/secure/passenger/send-email`, data, 'page-center');
  }

  refundBookingAmount(tourId, data): Observable<any> {
    return this.restApiService.post(`/secure/tour/refund/` + tourId, data, 'page-center');
  }

  /*Manual booking API */
  getPassengersList(searchString?, eventId?): Observable<any> {
    return this.restApiService.get('/secure/admin-booking/passenger-list?searchString=' + searchString + '&eventId=' + eventId);
  }

  getDriversList(srcLat, srcLng, destLat, DestLng, numOfSeats, rideTypeId, vehicleTypeId,
    searchString?, pickUpDateRideLater?, serviceTypeId?): Observable<any> {
    return this.restApiService.get('/secure/admin-booking/driver-list?rideTypeId=' + rideTypeId + '&pickupLat=' + srcLat +
      '&pickupLong=' + srcLng + '&destLat=' + destLat + '&destLong=' + DestLng + '&numOfSeats=' +
      numOfSeats + '&vehicleTypeId=' + vehicleTypeId + '&searchString=' + searchString + '&pickupDateTime='
      + pickUpDateRideLater + '&limit=10&offset=0' + '&serviceTypeId=' + serviceTypeId);
  }

  /**
   * @deprecated Legacy `callFrom` request, rejected by the strict pre-booking contract (TN-manage-booking-101-BE).
   * Only Bulk Load Bookings (release blocker, no Event yet) and the unrouted old Manual/Edit Manual Booking
   * components still use it. Use {@link getPreBookingMetadata}.
   */
  getPreBookingDetails(eventID?, callFrom?): Observable<any> {
    return this.restApiService.get(`/secure/admin-booking/pre-booking/data?eventId=${eventID}&callFrom=${callFrom}`, 'page-center');
  }

  getPreBookingMetadata(eventId: string, workflow: BookingWorkflow): Observable<any> {
    return this.restApiService.get(`/secure/admin-booking/pre-booking/data?eventId=${eventId}&workflow=${workflow}`, 'page-center');
  }

  /**
   * TN-manage-booking-102-BE §3.3: whether a Google-selected point lies within an active Airport radius of the event.
   * Read-only and loader-free, so picking a location does not flash the page spinner.
   */
  getAirportEligibility(eventId: string, latitude: number, longitude: number): Observable<AirportEligibility> {
    return this.restApiService.getInBackground(`/secure/admin-booking/airport-eligibility?eventId=${encodeURIComponent(eventId)}`
      + `&latitude=${latitude}&longitude=${longitude}`) as Observable<AirportEligibility>;
  }

  /** TN-manage-booking-211-FE: saves one contact of the passenger, optionally opting in for that channel (104-BE §3.7). */
  updatePassengerContact(passengerId: string, eventId: string, body: PassengerContactUpdate): Observable<{}> {
    return this.restApiService.put(`/secure/admin-booking/passenger-contact/${passengerId}?eventId=${encodeURIComponent(eventId)}`,
      body, 'page-center');
  }

  /**
   * TN-manage-booking-110-BE §3.1: whether a picked Pickup / Stop / Drop-off lies inside a dispatch zone of the event.
   * Read-only and loader-free, like the airport lookup, so a pick does not flash the page spinner.
   */
  getDispatchZone(eventId: string, latitude: number, longitude: number): Observable<DispatchZoneResult> {
    return this.restApiService.getInBackground(`/secure/admin-booking/dispatch-zone?eventId=${encodeURIComponent(eventId)}`
      + `&latitude=${latitude}&longitude=${longitude}`) as Observable<DispatchZoneResult>;
  }

  /**
   * TN-manage-booking-113-BE §3.2 / 221-FE §4.1 step 4: capacity of the pickup's slot before the Create confirmation.
   * Runs with the page spinner, like the fare estimate it follows.
   */
  getBookingCapacity(query: BookingCapacityQuery): Observable<BookingCapacityResult> {
    let url = `/secure/admin-booking/booking-capacity?eventId=${encodeURIComponent(query.eventId)}`
      + `&pickupLatitude=${query.pickupLatitude}&pickupLongitude=${query.pickupLongitude}&rideLater=${query.rideLater}`;
    if (query.rideLater && query.pickupLocalDateTime) {
      url += `&pickupLocalDateTime=${encodeURIComponent(query.pickupLocalDateTime)}`;
    }
    if (query.excludeTourId) {
      url += `&excludeTourId=${encodeURIComponent(query.excludeTourId)}`;
    }
    return this.restApiService.get(url, 'page-center') as Observable<BookingCapacityResult>;
  }

  /**
   * TN-manage-booking-227-FE §4.5 / §6.3: the Vehicle Transfer driver list with the Team, Duty Status, Check-In and search
   * filters. Read-only and loader-free, so typing in the popup search does not flash the page spinner.
   */
  getVehicleTransferDrivers(eventId: string, filters: VtFilters, search = ''): Observable<VehicleTransferDriver[]> {
    let url = `/secure/drivers/vehicle-transfer/list?eventId=${encodeURIComponent(eventId)}&dutyStatus=${filters.dutyStatus}`;
    if (filters.teamId) {
      url += `&teamId=${encodeURIComponent(filters.teamId)}`;
    }
    if (filters.checkInLocationId) {
      url += `&checkInLocationId=${encodeURIComponent(filters.checkInLocationId)}`;
    }
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    return this.restApiService.getInBackground(url) as Observable<VehicleTransferDriver[]>;
  }

  /** 227-FE §6.3: the event's teams (shifts); the drivers module's service is module-scoped, so Bookings reuses the endpoint. */
  getTeams(eventId: string): Observable<{ id: string; name: string; dutyStatus?: boolean }[]> {
    return (this.restApiService.getInBackground(`/secure/shift-management/shifts?projection=non-grid&eventId=${encodeURIComponent(eventId)}`) as
      Observable<{ id: string; name: string; dutyStatus?: boolean }[] | { shiftsList?: { id: string; name: string; dutyStatus?: boolean }[] }>)
      .pipe(map((result) => Array.isArray(result) ? result : result?.shiftsList ?? []));
  }

  /** 227-FE §6.3: the event's Check-In (parking) locations. */
  getCheckInLocations(eventId: string): Observable<{ locationList: { locationId: string; displayName?: string; locationCode?: string; activated?: boolean }[] }> {
    return this.restApiService.getInBackground(`/secure/location-list?eventId=${encodeURIComponent(eventId)}&locationCategoryIds=${AppSettings.PARKING_LOCATION_ID}`) as
      Observable<{ locationList: { locationId: string; displayName?: string; locationCode?: string; activated?: boolean }[] }>;
  }

  getFareEstimate(servicetypeid, data): Observable<any> {
    return this.restApiService.post(`/secure/booking/${servicetypeid}/fare-estimation`, data, 'page-center');
  }

  validatesupervisorPasscode(data): Observable<any> {
    return this.restApiService.post(`/secure/users/validate-passcode`, data, 'page-center');
  }

  bookRideNowAndRideLater(servicetypeid, data): Observable<any> {
    return this.restApiService.post(`/secure/booking/manual/${servicetypeid}`, data, 'page-center');
  }

  UpdateBookingRideNowAndRideLater(servicetypeid, prevBookingId, data): Observable<any> {
    return this.restApiService.put(`/secure/booking/manual/${prevBookingId}`, data, 'page-center');
  }

  replaceBooking(servicetypeid, data, bookingId): Observable<any> {
    return this.restApiService.post(`/secure/replace-booking/manual/${bookingId}`, data, 'page-center');
  }

  getNearByVehicles(sLatitude, sLongitude, dLatitude, dLongitude, radius, serviceTypeId, vehicleTypeId): Observable<any> {
    return this.restApiService.get(`/secure/vehicles/data/nearbyvehicles/eta?sLatitude=${sLatitude}&sLongitude=${sLongitude}` +
      `&dLatitude=${dLatitude}&dLongitude=${dLongitude}&radius=${radius}&serviceTypeId=${serviceTypeId}` +
      `&vehicleTypeId=${vehicleTypeId}`, 'page-center');
  }

  getVehicleTypeByServiceId(serviceId?): Observable<any> {
    return this.restApiService.get('/secure/vehicle-types?serviceTypeIds=' + serviceId, 'page-center');
  }

  getpackages(vehicleTypeId, sLatitude, sLongitude): Observable<any> {
    return this.restApiService.get(`/secure/package/list?vehicleTypeId=${vehicleTypeId}` +
      `&sLatitude=${sLatitude}&sLongitude=${sLongitude}`, 'page-center');
  }

  getAssignDriverList(): Observable<any> {
    return this.restApiService.get(`/secure/drivers/list/data`, 'page-center');
  }

  getAssignTripDetails(bookingId, params): Observable<any> {
    return this.restApiService.get(`/secure/drivers/assign-trip-v3/${bookingId}/list/data${params}`, 'page-center');
  }
  getFlightDetailsUpdate(tourId): Observable<any> {
    return this.restApiService.put(`/secure/flight-details/${tourId}`)
  }

  updateLuggageBagCount(data): Observable<any> {
    return this.restApiService.post(`/secure/tour/luggage-booking`, data, 'page-center');
  }

  mergeBookings(eventId, data): Observable<any> {
    return this.restApiService.post(`/secure/merge-rides/merge?eventId=${eventId}`, data, 'page-center');
  }
  unmergeBookings(eventId, data): Observable<any> {
    return this.restApiService.put(`/secure/merge-rides/unmerge?eventId=${eventId}`, data, 'page-center');
  }

  getShuttleSchedules(data, eventId?): Observable<any> {
    return this.restApiService.put(`/secure/bus-schedules-tours/list/v2?limit=50&offset=0&eventId=${eventId}`, data, 'page-center');
  }

  getShuttleScheduleDetails(scheduleTourId): Observable<any> {
    return this.restApiService.get(`/secure/bus-schedules-tour-details/${scheduleTourId}`, 'page-center');
  }

  bookShuttleSchedule(bookingData): Observable<any> {
    return this.restApiService.post('/secure/admin-bus-bookings', bookingData, 'page-center');
  }

  updateDedicatedBookingInfo(data, tourId): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/edit-details/${tourId}`, data, 'page-center');
  }

  getAssignedVehiclesList(vehicleTypeId, availableForService): Observable<any> {
    return this.restApiService.get(`/secure/vehicles-list/by-available-for-service?vehicleTypeId=${vehicleTypeId}&asPerToVehicleTypeOnly=&tenantServiceAreaId=&availableForService=${availableForService}&offset=&limit=`, 'page-center');
  }

  getAssignedDriverList(availableForService, dedicatedTourDate, dedicatedDayWisePickUpDateTime, daywiseTourId): Observable<any> {
    return this.restApiService.get(`/secure/drivers-list/by-available-for-service?tenantServiceAreaId=&availableForService=${availableForService}&dedicatedTourDate=${dedicatedTourDate}&dedicatedDayWisePickUpDateTime=${dedicatedDayWisePickUpDateTime}&daywiseTourId=${daywiseTourId}&offset=&limit=`, 'page-center');
  }

  createDedicatedJourney(data): Observable<any> {
    return this.restApiService.post(`/secure/dedicated-bookings/create-journey`, data, 'page-center');
  }

  changeDedicatedBookingStatus(dedicatedTourId, data): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/status/${dedicatedTourId}`, data, 'page-center');
  }

  getDedicatedBookingDetails(dedicatedTourId): Observable<any> {
    return this.restApiService.get(`/secure/dedicated-bookings/details/${dedicatedTourId}`, 'page-center');
  }

  getDedicatedTimeline(dedicatedTourId, date, dedicatedTourDaywiseDataId): Observable<any> {
    return this.restApiService.get(`/secure/dedicated-bookings/tour-timeline/${dedicatedTourId}?date=${date}&dedicatedTourDaywiseDataId=${dedicatedTourDaywiseDataId}`, 'page-center');
  }

  assignDriverToDedicated(dedicatedTourDaywiseDataId, data, type, requestedStartDatetimeStr, requestedEndDatetimeStr): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/assign-driver/${dedicatedTourDaywiseDataId}/${type}?requestedStartDatetimeStr=${requestedStartDatetimeStr}&requestedEndDatetimeStr=${requestedEndDatetimeStr}`, data, 'page-center');
  }

  assignVehicleToDedicated(dedicatedTourDaywiseDataId, data, type, requestedStartDatetimeStr, requestedEndDatetimeStr): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/assign-vehicle/${dedicatedTourDaywiseDataId}/${type}?requestedStartDatetimeStr=${requestedStartDatetimeStr}&requestedEndDatetimeStr=${requestedEndDatetimeStr}`, data, 'page-center');
  }

  assignDriverAndVehicleToDedicated(dedicatedTourDaywiseDataId, data, assignmentType, requestedStartDatetimeStr, requestedEndDatetimeStr): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/assign-driver-vehicle/${dedicatedTourDaywiseDataId}?requestedStartDatetimeStr=${requestedStartDatetimeStr}&requestedEndDatetimeStr=${requestedEndDatetimeStr}`, data, 'page-center');
  }

  removeDriverVehicleFromDedicated(dedicatedTourDaywiseDataId, data): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/remove-driver-vehicle/${dedicatedTourDaywiseDataId}`, data, 'page-center');
  }

  changeDayWiseTourStatus(dedicatedTourDaywiseDataId, data): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/daywise-tour-status/${dedicatedTourDaywiseDataId}`, data, 'page-center');
  }

  changeStopWiseBookingStatus(dedicatedTourLocationId, data): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/intermediate-location-status/${dedicatedTourLocationId}`, data, 'page-center');
  }

  getDedicatedFareEstimation(data): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/tenant-service-area/get-by-location`, data, 'page-center');
  }

  addNewStopLocation(data): Observable<any> {
    return this.restApiService.post(`/secure/dedicated-bookings/add-intermediate-location`, data, 'page-center');
  }

  updateStopLocation(dedicatedTourLocationId, data): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/update-intermediate-location/${dedicatedTourLocationId}`, data, 'page-center');
  }

  deleteStopLocation(dedicatedTourLocationId, data): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/delete-intermediate-location/${dedicatedTourLocationId}`, data, 'page-center');
  }

  getDedicatedBookingActivities(dedicatedTourId): Observable<any> {
    return this.restApiService.get(`/secure/dedicated-bookings/activity-grid-data?dedicatedTourId=${dedicatedTourId}`, 'page-center');
  }

  updateDayTimelineData(dedicatedTourDaywiseDataId, data): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/update-nextday-pickup/${dedicatedTourDaywiseDataId}`, data, 'page-center');
  }

  extendDedicatedJourney(data): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/extend-journey`, data, 'page-center');
  }

  getPassengerType(eventId = ''): Observable<any> {
    return this.restApiService.get(`/secure/privilege-settings/data-grid?eventId=${eventId}&offset=0&limit=`, 'page-center');
  }

  getDriverBusyStatus(data): Observable<any> {
    return this.restApiService.put(`/secure/dedicated-bookings/check-driver-busy`, data, 'page-center');
  }

}
